import PropTypes from 'prop-types'
import React, { Fragment, useMemo } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useTranslation } from 'react-i18next'

import ButtonGroup from '@material-ui/core/ButtonGroup'
import { makeStyles } from '@material-ui/core/styles'

import PauseIcon from '@material-ui/icons/Pause'
import PlayArrowIcon from '@material-ui/icons/PlayArrow'
import StopIcon from '@material-ui/icons/Stop'

import ControlButton from './ControlButton'

const styles = {
  container: {
    display: 'flex',
  },
}
const useStyles = makeStyles(styles)

function useControlsHotkeys(keys, callback, deps) {
  return useHotkeys(
    keys,
    (event, handler) => {
      event.preventDefault()

      callback(event, handler)
    },
    {},
    deps,
  )
}

function usePrimaryControl(params) {
  const { isPaused, isPlaying, isStopped, pause, play, resume, t } = params

  return useMemo(() => {
    const canPause = isPlaying
    const canResume = isPaused
    const canPlay = isStopped

    if (canPause) {
      return {
        Icon: PauseIcon,
        disabled: false,
        label: t('player:controls-buttons-pause'),
        onClick: pause,
      }
    }

    if (canResume) {
      return {
        Icon: PlayArrowIcon,
        disabled: false,
        label: t('player:controls-buttons-resume'),
        onClick: resume,
      }
    }

    return {
      Icon: PlayArrowIcon,
      disabled: !canPlay,
      label: t('player:controls-buttons-play'),
      onClick: play,
    }
  }, [isPaused, isPlaying, isStopped, pause, play, resume, t])
}

function useStopControl(params) {
  const { isPaused, isPlaying, stop, t } = params

  return useMemo(() => {
    const canStop = isPlaying || isPaused

    return {
      Icon: StopIcon,
      disabled: !canStop,
      label: t('player:controls-buttons-stop'),
      onClick: stop,
    }
  }, [isPaused, isPlaying, stop, t])
}

export default function Controls(props) {
  const {
    disabled = false,
    isPaused,
    isPlaying,
    isStopped,
    labels = false,
    pause,
    play,
    resume,
    stop,
  } = props

  const { t } = useTranslation()

  const primaryControl = usePrimaryControl({
    isPaused,
    isPlaying,
    isStopped,
    pause,
    play,
    resume,
    t,
  })

  const stopControl = useStopControl({
    isPlaying,
    isPaused,
    stop,
    t,
  })

  useControlsHotkeys(
    'space',
    () => {
      if (!disabled && !primaryControl.disabled) {
        primaryControl.onClick()
      }
    },
    [primaryControl],
  )

  const classes = useStyles()

  return (
    <Fragment>
      <ButtonGroup className={classes.container}>
        <ControlButton
          Icon={primaryControl.Icon}
          color="primary"
          disabled={disabled || primaryControl.disabled}
          label={labels ? primaryControl.label : null}
          onClick={primaryControl.onClick}
          variant="contained"
        />

        <ControlButton
          Icon={stopControl.Icon}
          color="default"
          disabled={disabled || stopControl.disabled}
          label={labels ? stopControl.label : null}
          onClick={stopControl.onClick}
          variant="contained"
        />
      </ButtonGroup>
    </Fragment>
  )
}

Controls.propTypes = {
  disabled: PropTypes.bool,
  isPaused: PropTypes.bool.isRequired,
  isPlaying: PropTypes.bool.isRequired,
  isStopped: PropTypes.bool.isRequired,
  labels: PropTypes.bool,
  pause: PropTypes.func.isRequired,
  play: PropTypes.func.isRequired,
  resume: PropTypes.func.isRequired,
  stop: PropTypes.func.isRequired,
}
